import { Link } from 'react-router-dom';
import WigEmoticon from './WigEmoticon';

interface FooterLink {
  name: string;
  href: string;
  external?: boolean;
}

interface FooterSection {
  title: string;
  links: FooterLink[];
}

function Footer() {
  const currentYear = new Date().getFullYear();

  const footerSections: FooterSection[] = [
    {
      title: 'Legal',
      links: [
        { name: 'Terms of Service', href: '/legal/terms' },
        { name: 'Privacy Policy', href: '/legal/privacy' },
        { name: 'Community Guidelines', href: '/legal/guidelines' }
      ]
    },
    {
      title: 'Support',
      links: [
        { name: 'How It Works', href: '/how-it-works' },
        { name: 'Help Center', href: '/help' },
        { name: 'Safety Tips', href: '/resources' },
        { name: 'Contact Support', href: '/contact' }
      ]
    },
    {
      title: 'Company',
      links: [
        { name: 'About Us', href: '/about' },
        { name: 'Rewards Program', href: '/rewards-terms' },
        { name: 'Press', href: '/press' },
        { name: 'MyHairMail', href: 'https://myhairmail.com/', external: true }
      ]
    }
  ];

  return (
    <footer className="bg-white border-t">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {/* Brand Section */}
          <div className="col-span-2 md:col-span-1">
            <Link to="/">
              <div className="flex items-center gap-2">
                <WigEmoticon />
              <div className="text-2xl font-bold text-primary-600">WigConnection</div>
              </div>
              <p className="mt-2 text-sm text-gray-500">
                The trusted marketplace for premium wigs
              </p>
            </Link>
          </div>

          {/* Footer Sections */}
          {footerSections.map((section) => (
            <div key={section.title}>
              <h3 className="text-sm font-semibold text-gray-900 tracking-wider uppercase">
                {section.title}
              </h3>
              <ul className="mt-4 space-y-3">
                {section.links.map((link) => (
                  <li key={link.name}>
                    {link.external ? (
                      <a
                        href={link.href}
                        className="text-sm text-gray-600 hover:text-primary-600 transition-colors"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {link.name}
                      </a>
                    ) : (
                      <Link
                        to={link.href}
                        className="text-sm text-gray-600 hover:text-primary-600 transition-colors"
                      >
                        {link.name}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom Section */}
        <div className="mt-12 pt-8 border-t border-gray-200">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className="text-sm text-gray-500">
              © {currentYear} WigConnection.com. All rights reserved.
            </p>
            <div className="mt-4 md:mt-0 flex space-x-6">
              <Link
                to="/legal/accessibility"
                className="text-sm text-gray-500 hover:text-primary-600 transition-colors"
              >
                Accessibility
              </Link>
              <Link
                to="/legal/cookies"
                className="text-sm text-gray-500 hover:text-primary-600 transition-colors"
              >
                Cookie Policy
              </Link>
              <Link
                to="/sitemap"
                className="text-sm text-gray-500 hover:text-primary-600 transition-colors"
              >
                Sitemap
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default Footer;